import axios from 'axios';
import {
  GetStripePayoutsParams,
  Organization,
  StripeConnectExpressAccount,
  StripePayoutPage,
} from './models/Organization';
import { CreateSpaceRequest, Space, SpaceWithAmenitiesAndGears, UpdateSpaceRequest } from './models/Space';
import {
  Room,
  HostRoom,
  CreateRoomRequest,
  UpdateRoomRequest,
  RoomWithAmenitiesAndGears,
  ReorderRoomsRequest,
  UpdateRoomVisibilityRequest,
} from './models/Room';
import { OrganizationRequest } from './models/OrganizationRequest';
import { CreateResponse } from './models/CreateReponse';
import { GenerateUploadUrlsRequest } from './models/GenerateUploadUrlsRequest';
import { GenerateUploadUrlsResponse } from './models/GenerateUploadUrlsResponse';
import { HostRoomBooking, Page, BookingDecisionRequest } from './models/HostRoomBooking';
import { RoomBookingDetails } from './models/RoomBookingDetails';
import { CalendarEvent, CalendarEventWithMemberInfo } from './models/CalendarEvent';
import { CreateManualRoomBookingRequest, CreateRoomBookingRequest, CreateRoomBookingResponse, RoomBookingNeedsHostActionCountResponse } from './models/RoomBooking';
import { RefundRoomBookingRequest } from './models/RefundRoomBookingRequest';
import { HostProfile } from './models/Profile';
import {
  GoogleCalendarOption,
  GoogleCalendarConsentLink,
  GoogleCalendarIntegration,
  UpdateRoomCalendarRequest,
  UpdateTutorCalendarRequest,
} from './models/GoogleCalendar';
import {
  CashCommissionChargesResponse,
  ChangeSubscriptionPlanRequest,
  CheckoutSubscriptionResponse,
  CommissionChargesResponse,
  CreateSubscriptionCheckoutRequest,
  CreateSubscriptionPortalRequest,
  GetCommissionChargesParams,
  GetSubscriptionInvoicesParams,
  OrganizationSubscriptionStatus,
  SubscriptionInvoicePage,
  SubscriptionPaymentMethodResponse,
  SubscriptionPlanChangeResponse,
  SubscriptionPlanSummary,
  SubscriptionPortalResponse,
} from './models/Subscription';
import { ProposeChangeRequest, CancelChangeRequest } from './models/RoomBookingChange';
import {
  CancelTutorBookingChangeRequest,
  ProposeTutorBookingChangeRequest,
} from './models/TutorBookingChange';
import { HostOrganizationUserResponse } from './models/HostOrganizationUser';
import {
  HostStudent,
  StudentProfile,
  UpdateStudentProfileRequest,
  CreateHostStudentRequest,
  CreateHostStudentResponse,
} from './models/HostStudent';
import {
  CreateHostTutorBookingRequest,
  CreateManualTutorBookingResponse,
  HostTutorBookingResponse,
  HostTutorBookingRoomAssignmentRequest,
  TutorBookingFilter,
  TutorBookingNeedsTutorActionCountResponse,
  TutorBookingSummaryCountsResponse,
  UserTutorBookingDetails,
} from './models/Tutor';
import { HostMonthlyEarningsResponse } from './models/Earnings';
import {
  GenerateSmartLockPasscodeRequest,
  GenerateSmartLockPasscodeResponse,
  LinkRoomToSmartLockRequest,
  LinkedRoomLockResponse,
  LinkedRoomsByLockResponse,
  SmartLockSettingsResponse,
  SmartLockSummary,
  UpsertSmartLockSettingsRequest,
} from './models/SmartLock';
import {
  PassOfferDetail,
  PassOfferStatus,
  PassOfferSummary,
  PassOfferUpsertRequest,
  PassProviderDetail,
  PassProviderEvent,
  PassProviderInstanceSummary,
} from './models/Pass';
import {
  LessonOptionDetailResponse,
  LessonOptionStatus,
  LessonOptionSummaryResponse,
  LessonOptionUpsertRequest,
} from './models/LessonOption';
import { applyClientChannelHeader } from './utils/clientChannel';
import { applyAllowTestOrgsHeader } from './utils/experimentalFeatures';

// Define the base API URL based on the environment
const baseURL = process.env.REACT_APP_API_BASE_URL + "/host";

class HostApiClient {
  private axiosInstance;

  constructor() {
    this.axiosInstance = axios.create({
      baseURL,
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
    });

    this.axiosInstance.interceptors.request.use(
      (config) => {
        const accessToken = localStorage.getItem("access_token");
        const assumedOrganizationId = localStorage.getItem("assumed_organization_id");
    
        if (accessToken) {
          config.headers['Authorization'] = `Bearer ${accessToken}`;
        } 

        if (assumedOrganizationId) {
          config.headers['X-OrganizationId'] = assumedOrganizationId;
        }

        applyClientChannelHeader(config.headers);
        applyAllowTestOrgsHeader(config.headers);
    
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    this.axiosInstance.interceptors.response.use(
      (response) => response,
      (error) => this.handleErrorResponse(error)
    );
  }

  private handleErrorResponse(error: any): Promise<never> {
    const status = error.response ? error.response.status : null;

    // Only clear session on 401 (invalid/expired token). 403 is authorization, not auth failure.
    if (status === 401) {
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");
      localStorage.removeItem("phone_number");
    }

    return Promise.reject(error);
  }

  public async getOrganization(): Promise<Organization> {
    try {
      const response = await this.axiosInstance.get<Organization>(`/organization`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching organization`, error);
      throw error;
    }
  }

  public async updateOrganization(updateOrganizationRequest: OrganizationRequest): Promise<void> {
    try {
      await this.axiosInstance.put<void>('/organization', updateOrganizationRequest);
    } catch (error) {
      console.error(`Error editing organization`, error);
      throw error;
    }
  }

  public async getSpaces(): Promise<Space[]> {
    try {
      const response = await this.axiosInstance.get<Space[]>('/spaces');
      return response.data;
    } catch (error) {
      console.error('Error fetching spaces:', error);
      throw error;
    }
  }

  public async createSpace(request: CreateSpaceRequest): Promise<CreateResponse> {
    try {
      const response = await this.axiosInstance.post<CreateResponse>('/spaces', request);
      return response.data;
    } catch (error) {
      console.error('Error creating space:', error);
      throw error;
    }
  }

  public async getSpace(id: string): Promise<SpaceWithAmenitiesAndGears> {
    try {
      const response = await this.axiosInstance.get<SpaceWithAmenitiesAndGears>(`/spaces/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching space with id ${id}:`, error);
      throw error;
    }
  }

  public async updateSpace(id: string, request: UpdateSpaceRequest): Promise<void> {
    try {
      await this.axiosInstance.put<void>(`/spaces/${id}`, request);
    } catch (error) {
      console.error(`Error updating space with id ${id}:`, error);
      throw error;
    }
  }

  public async deleteSpace(id: string): Promise<void> {
    try {
      await this.axiosInstance.delete<void>(`/spaces/${id}`);
    } catch (error) {
      console.error(`Error deleting space with id ${id}:`, error);
      throw error;
    }
  }

  public async getRooms(spaceId?: string): Promise<HostRoom[]> {
    try {
      const response = await this.axiosInstance.get<HostRoom[]>('/rooms', {
        params: spaceId ? { spaceId } : {},
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching rooms:', error);
      throw error;
    }
  }

  /** PUT /v1/host/spaces/{spaceId}/rooms/reorder — replace order for every room in one space. */
  public async reorderRooms(spaceId: string, request: ReorderRoomsRequest): Promise<void> {
    try {
      await this.axiosInstance.put<void>(`/spaces/${spaceId}/rooms/reorder`, request);
    } catch (error) {
      console.error(`Error reordering rooms for space ${spaceId}:`, error);
      throw error;
    }
  }

  /** PUT /v1/host/rooms/{roomId}/visibility — update one room's public visibility. */
  public async updateRoomVisibility(roomId: string, request: UpdateRoomVisibilityRequest): Promise<void> {
    try {
      await this.axiosInstance.put<void>(`/rooms/${roomId}/visibility`, request);
    } catch (error) {
      console.error(`Error updating visibility for room ${roomId}:`, error);
      throw error;
    }
  }

  public async createRoom(request: CreateRoomRequest): Promise<CreateResponse> {
    try {
      const response = await this.axiosInstance.post<CreateResponse>('/rooms', request);
      return response.data;
    } catch (error) {
      console.error('Error creating room', error);
      throw error;
    }
  }

  public async getRoom(id: string): Promise<RoomWithAmenitiesAndGears> {
    try {
      const response = await this.axiosInstance.get<RoomWithAmenitiesAndGears>(`/rooms/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching room with id ${id}:`, error);
      throw error;
    }
  }

  public async updateRoom(id: string, request: UpdateRoomRequest): Promise<void> {
    try {
      await this.axiosInstance.put<Room>(`/rooms/${id}`, request);
    } catch (error) {
      console.error(`Error updating room with id ${id}:`, error);
      throw error;
    }
  }

  public async deleteRoom(id: string): Promise<void> {
    try {
      await this.axiosInstance.delete<Room>(`/rooms/${id}`);
    } catch (error) {
      console.error(`Error deleting room with id ${id}:`, error);
      throw error;
    }
  }

  public async generateUploadUrls(request: GenerateUploadUrlsRequest): Promise<GenerateUploadUrlsResponse> {
    try {
      const response = await this.axiosInstance.post<GenerateUploadUrlsResponse>(
        "/assets/upload-urls",
        request
      );
      return response.data;
    } catch (error) {
      console.error("Error generating asset upload URLs:", error);
      throw error;
    }
  }

  public async uploadToPresignedUrl(url: string, file: File, onProgress?: (progress: number) => void): Promise<void> {
    await axios.put(url, file, {
      headers: {
        "Content-Type": file.type,
      },
      onUploadProgress: (e) => {
        if (e.total) {
          const progress = Math.round((e.loaded * 100) / e.total);
          onProgress?.(progress);
        }
      },
    });
  }

  public async getHostRoomBookings(page: number = 0, size: number = 10): Promise<Page<HostRoomBooking>> {
    try {
      const response = await this.axiosInstance.get<Page<HostRoomBooking>>('/room-bookings', {
        params: {
          page,
          size,
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching host room bookings:', error);
      throw error;
    }
  }

  /** GET /v1/host/room-bookings/{bookingId} */
  public async getHostRoomBookingDetails(bookingId: string): Promise<RoomBookingDetails> {
    try {
      const response = await this.axiosInstance.get<RoomBookingDetails>(`/room-bookings/${bookingId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching host room booking details:', error);
      throw error;
    }
  }

  /** GET /v1/host/room-bookings/needs-host-action/count — actionable badge total only. */
  public async getHostRoomBookingsNeedsHostActionCount(): Promise<RoomBookingNeedsHostActionCountResponse> {
    try {
      const response = await this.axiosInstance.get<RoomBookingNeedsHostActionCountResponse>(
        '/room-bookings/needs-host-action/count',
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching host room bookings needs-host-action count:', error);
      throw error;
    }
  }

  public async getHostTutorBookings(
    page: number = 0,
    size: number = 10,
    filter: TutorBookingFilter = 'all',
  ): Promise<Page<HostTutorBookingResponse>> {
    try {
      const response = await this.axiosInstance.get<Page<HostTutorBookingResponse>>('/tutor-bookings', {
        params: { page, size, filter },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching host tutor bookings:', error);
      throw error;
    }
  }

  /** GET /v1/host/tutor-bookings/{bookingId} */
  public async getHostTutorBookingDetails(bookingId: string): Promise<UserTutorBookingDetails> {
    try {
      const response = await this.axiosInstance.get<UserTutorBookingDetails>(`/tutor-bookings/${bookingId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching host tutor booking details:', error);
      throw error;
    }
  }

  /** POST /v1/host/tutor-bookings — walk-in lesson; returns booking id only. */
  public async createHostTutorWalkInBooking(
    request: CreateHostTutorBookingRequest,
  ): Promise<{ id: string }> {
    try {
      const response = await this.axiosInstance.post<{ id: string }>('/tutor-bookings', request);
      return response.data;
    } catch (error) {
      console.error('Error creating host tutor booking:', error);
      throw error;
    }
  }

  /** POST /v1/host/tutor-bookings/manual — host manual booking with shareable link. */
  public async createManualTutorBooking(
    request: CreateHostTutorBookingRequest,
  ): Promise<CreateManualTutorBookingResponse> {
    try {
      const response = await this.axiosInstance.post<CreateManualTutorBookingResponse>(
        '/tutor-bookings/manual',
        request,
      );
      return response.data;
    } catch (error) {
      console.error('Error creating host manual tutor booking:', error);
      throw error;
    }
  }

  /** GET /v1/host/tutor-bookings/summary — tab badge counts. */
  public async getHostTutorBookingsSummary(): Promise<TutorBookingSummaryCountsResponse> {
    try {
      const response = await this.axiosInstance.get<TutorBookingSummaryCountsResponse>('/tutor-bookings/summary');
      return response.data;
    } catch (error) {
      console.error('Error fetching host tutor bookings summary:', error);
      throw error;
    }
  }

  /** GET /v1/host/tutor-bookings/needs-tutor-action/count — actionable badge total only. */
  public async getHostTutorBookingsNeedsTutorActionCount(): Promise<TutorBookingNeedsTutorActionCountResponse> {
    try {
      const response = await this.axiosInstance.get<TutorBookingNeedsTutorActionCountResponse>(
        '/tutor-bookings/needs-tutor-action/count',
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching host tutor bookings needs-tutor-action count:', error);
      throw error;
    }
  }

  public async acceptTutorBooking(bookingId: string): Promise<void> {
    await this.axiosInstance.post<void>(`/tutor-bookings/${bookingId}/accept`);
  }

  public async declineTutorBooking(bookingId: string, reason: string): Promise<void> {
    await this.axiosInstance.post<void>(`/tutor-bookings/${bookingId}/decline`, { reason });
  }

  public async acceptTutorBookingSeries(recurringSeriesId: string): Promise<void> {
    await this.axiosInstance.post<void>(`/tutor-bookings/series/${recurringSeriesId}/accept`);
  }

  public async acceptSelectedTutorBookingSeries(
    recurringSeriesId: string,
    bookingIds: string[],
    declineReason: string,
  ): Promise<void> {
    await this.axiosInstance.post<void>(`/tutor-bookings/series/${recurringSeriesId}/accept-selected`, {
      bookingIds,
      declineReason,
    });
  }

  public async declineTutorBookingSeries(recurringSeriesId: string, reason: string): Promise<void> {
    await this.axiosInstance.post<void>(`/tutor-bookings/series/${recurringSeriesId}/decline`, { reason });
  }

  /** POST /v1/host/tutor-bookings/{bookingId}/changes */
  public async proposeTutorBookingChange(
    bookingId: string,
    request: ProposeTutorBookingChangeRequest,
  ): Promise<void> {
    try {
      await this.axiosInstance.post<void>(`/tutor-bookings/${bookingId}/changes`, request);
    } catch (error) {
      console.error(`Error proposing change for tutor booking ${bookingId}:`, error);
      throw error;
    }
  }

  /** POST /v1/host/tutor-bookings/{bookingId}/changes/{changeId}/cancel */
  public async cancelTutorBookingChange(
    bookingId: string,
    changeId: string,
    request: CancelTutorBookingChangeRequest,
  ): Promise<void> {
    try {
      await this.axiosInstance.post<void>(
        `/tutor-bookings/${bookingId}/changes/${changeId}/cancel`,
        request,
      );
    } catch (error) {
      console.error(`Error cancelling tutor booking change ${changeId} for booking ${bookingId}:`, error);
      throw error;
    }
  }

  /** POST /v1/host/tutor-bookings/{bookingId}/refund */
  public async refundTutorBooking(bookingId: string, request: RefundRoomBookingRequest = {}): Promise<void> {
    try {
      await this.axiosInstance.post<void>(`/tutor-bookings/${bookingId}/refund`, request);
    } catch (error) {
      console.error(`Error refunding tutor booking ${bookingId}:`, error);
      throw error;
    }
  }

  /** POST /v1/host/tutor-bookings/{bookingId}/collect-payment — mark on-site payment collected. */
  public async collectTutorBookingPayment(bookingId: string): Promise<void> {
    try {
      await this.axiosInstance.post<void>(`/tutor-bookings/${bookingId}/collect-payment`);
    } catch (error) {
      console.error(`Error collecting payment for tutor booking ${bookingId}:`, error);
      throw error;
    }
  }

  /** PUT /v1/host/tutor-bookings/{bookingId}/room — assign/reassign room for an offline lesson. */
  public async assignTutorBookingRoom(
    bookingId: string,
    request: HostTutorBookingRoomAssignmentRequest,
  ): Promise<void> {
    try {
      await this.axiosInstance.put<void>(`/tutor-bookings/${bookingId}/room`, request);
    } catch (error) {
      console.error(`Error assigning room for tutor booking ${bookingId}:`, error);
      throw error;
    }
  }

  /** DELETE /v1/host/tutor-bookings/{bookingId}/room — clear room assignment. */
  public async clearTutorBookingRoom(bookingId: string): Promise<void> {
    try {
      await this.axiosInstance.delete<void>(`/tutor-bookings/${bookingId}/room`);
    } catch (error) {
      console.error(`Error clearing room for tutor booking ${bookingId}:`, error);
      throw error;
    }
  }

  public async handleBookingDecision(bookingId: string, request: BookingDecisionRequest): Promise<void> {
    try {
      await this.axiosInstance.post<void>(`/room-bookings/${bookingId}/decision`, request);
    } catch (error) {
      console.error(`Error handling booking decision for booking ${bookingId}:`, error);
      throw error;
    }
  }

  public async acceptRoomBooking(bookingId: string): Promise<void> {
    try {
      await this.axiosInstance.post<void>(`/room-bookings/${bookingId}/accept`);
    } catch (error) {
      console.error(`Error accepting booking ${bookingId}:`, error);
      throw error;
    }
  }

  public async declineRoomBooking(bookingId: string, reason: string): Promise<void> {
    try {
      await this.axiosInstance.post<void>(`/room-bookings/${bookingId}/decline`, { reason });
    } catch (error) {
      console.error(`Error declining booking ${bookingId}:`, error);
      throw error;
    }
  }

  /** Accept all pending sessions in a recurring series (host). */
  public async acceptRoomBookingSeries(recurringSeriesId: string): Promise<void> {
    try {
      await this.axiosInstance.post<void>(`/room-bookings/series/${recurringSeriesId}/accept`);
    } catch (error) {
      console.error(`Error accepting recurring series ${recurringSeriesId}:`, error);
      throw error;
    }
  }

  /** Decline all pending sessions in a recurring series (host). */
  public async declineRoomBookingSeries(recurringSeriesId: string, reason: string): Promise<void> {
    try {
      await this.axiosInstance.post<void>(`/room-bookings/series/${recurringSeriesId}/decline`, { reason });
    } catch (error) {
      console.error(`Error declining recurring series ${recurringSeriesId}:`, error);
      throw error;
    }
  }

  /** Capture the selected online sessions and decline every other pending session. */
  public async acceptSelectedRoomBookingSeries(
    recurringSeriesId: string,
    bookingIds: string[],
    declineReason: string,
  ): Promise<void> {
    try {
      await this.axiosInstance.post<void>(
        `/room-bookings/series/${recurringSeriesId}/accept-selected`,
        { bookingIds, declineReason },
      );
    } catch (error) {
      console.error(`Error accepting selected sessions in series ${recurringSeriesId}:`, error);
      throw error;
    }
  }

  /** POST /v1/host/room-bookings/{bookingId}/refund */
  public async refundRoomBooking(bookingId: string, request: RefundRoomBookingRequest = {}): Promise<void> {
    try {
      await this.axiosInstance.post<void>(`/room-bookings/${bookingId}/refund`, request);
    } catch (error) {
      console.error(`Error refunding room booking ${bookingId}:`, error);
      throw error;
    }
  }

  /** POST /v1/host/room-bookings/{bookingId}/collect-payment — mark cash payment collected. */
  public async collectRoomBookingPayment(bookingId: string): Promise<void> {
    try {
      await this.axiosInstance.post<void>(`/room-bookings/${bookingId}/collect-payment`);
    } catch (error) {
      console.error(`Error collecting payment for room booking ${bookingId}:`, error);
      throw error;
    }
  }

  public async getSpaceCalendarEvents(spaceId: string, startTime: string, endTime: string): Promise<CalendarEvent[]> {
    try {
      const response = await this.axiosInstance.get<CalendarEvent[]>(`/spaces/${spaceId}/calendar-events?fromTime=${startTime}&toTime=${endTime}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching calendar events for space ${spaceId}:`, error);
      throw error;
    }
  }

  public async getRoomCalendarEventsWithMemberInfo(roomId: string, fromTime: string, toTime: string): Promise<CalendarEventWithMemberInfo[]> {
    try {
      const response = await this.axiosInstance.get<CalendarEventWithMemberInfo[]>(`/rooms/${roomId}/calendar-events`, {
        params: {
          fromTime,
          toTime,
        },
      });
      return response.data;
    } catch (error) {
      console.error(`Error fetching calendar events with member info for room ${roomId}:`, error);
      throw error;
    }
  }

  /** Host-created manual calendar blocks only (no room_booking). Use for delete. */
  public async getHostRoomCalendarEvents(roomId: string, fromTime: string, toTime: string): Promise<CalendarEvent[]> {
    try {
      const response = await this.axiosInstance.get<CalendarEvent[]>(`/rooms/${roomId}/calendar-events`, {
        params: { fromTime, toTime },
      });
      return response.data;
    } catch (error) {
      console.error(`Error fetching host room calendar events for room ${roomId}:`, error);
      throw error;
    }
  }

  /** Create manual block (calendar_event only, no room_booking). */
  public async createHostRoomCalendarEvent(
    roomId: string,
    request: { title: string; startTime: string; endTime: string }
  ): Promise<{ id: string }> {
    try {
      const response = await this.axiosInstance.post<{ id: string }>(`/rooms/${roomId}/calendar-events`, request);
      return response.data;
    } catch (error) {
      console.error(`Error creating host room calendar event for room ${roomId}:`, error);
      throw error;
    }
  }

  /** Delete manual block. Only for events from getHostRoomCalendarEvents. */
  public async deleteHostRoomCalendarEvent(roomId: string, calendarEventId: string): Promise<void> {
    try {
      await this.axiosInstance.delete(`/rooms/${roomId}/calendar-events/${calendarEventId}`);
    } catch (error) {
      console.error(`Error deleting host room calendar event ${calendarEventId}:`, error);
      throw error;
    }
  }

  public async getGoogleCalendarOptions(): Promise<GoogleCalendarOption[]> {
    try {
      const response = await this.axiosInstance.get<GoogleCalendarOption[]>('/google-calendar/options');
      return response.data;
    } catch (error) {
      console.error('Error fetching Google Calendar options:', error);
      throw error;
    }
  }

  public async getGoogleCalendarConsentLink(): Promise<GoogleCalendarConsentLink> {
    try {
      const response = await this.axiosInstance.get<GoogleCalendarConsentLink>('/google-calendar/consent-link');
      return response.data;
    } catch (error) {
      console.error('Error fetching Google Calendar consent link:', error);
      throw error;
    }
  }

  /** GET /v1/host/organization/google-calendar — org connection + per-calendar health. */
  public async getGoogleCalendarIntegration(): Promise<GoogleCalendarIntegration> {
    try {
      const response = await this.axiosInstance.get<GoogleCalendarIntegration>('/organization/google-calendar');
      return response.data;
    } catch (error) {
      console.error('Error fetching Google Calendar integration status:', error);
      throw error;
    }
  }

  /** POST /v1/host/google-calendar/disconnect — revoke org Google Calendar connection. */
  public async disconnectGoogleCalendar(): Promise<void> {
    try {
      await this.axiosInstance.post<void>('/google-calendar/disconnect');
    } catch (error) {
      console.error('Error disconnecting Google Calendar:', error);
      throw error;
    }
  }

  /** PUT /v1/host/rooms/{roomId}/color — update room calendar color only. */
  public async updateRoomColor(roomId: string, color: string): Promise<void> {
    try {
      await this.axiosInstance.put<void>(`/rooms/${roomId}/color`, { color });
    } catch (error) {
      console.error(`Error updating room color for room ${roomId}:`, error);
      throw error;
    }
  }

  /** PUT /v1/host/rooms/{roomId}/calendar — link or unlink room Google Calendar. */
  public async updateRoomCalendar(roomId: string, request: UpdateRoomCalendarRequest): Promise<void> {
    try {
      await this.axiosInstance.put<void>(`/rooms/${roomId}/calendar`, request);
    } catch (error) {
      console.error(`Error updating room calendar for room ${roomId}:`, error);
      throw error;
    }
  }

  /** PUT /v1/host/tutors/{tutorId}/color — update tutor calendar identity color. */
  public async updateTutorColor(tutorId: string, color: string): Promise<void> {
    try {
      await this.axiosInstance.put<void>(`/tutors/${tutorId}/color`, { color });
    } catch (error) {
      console.error(`Error updating tutor color for tutor ${tutorId}:`, error);
      throw error;
    }
  }

  /** PUT /v1/host/tutors/{tutorId}/calendar — link or unlink tutor Google Calendar. */
  public async updateTutorCalendar(tutorId: string, request: UpdateTutorCalendarRequest): Promise<void> {
    try {
      await this.axiosInstance.put<void>(`/tutors/${tutorId}/calendar`, request);
    } catch (error) {
      console.error(`Error updating tutor calendar for tutor ${tutorId}:`, error);
      throw error;
    }
  }

  /** Create manual tutor block (calendar_event only, no tutor_booking). */
  public async createHostTutorCalendarEvent(
    tutorId: string,
    request: { title: string; startTime: string; endTime: string },
  ): Promise<{ id: string }> {
    try {
      const response = await this.axiosInstance.post<{ id: string }>(
        `/tutors/${tutorId}/calendar-events`,
        request,
      );
      return response.data;
    } catch (error) {
      console.error(`Error creating host tutor calendar event for tutor ${tutorId}:`, error);
      throw error;
    }
  }

  /** DELETE /v1/host/tutors/{tutorId}/calendar-events/{calendarEventId} — manual tutor block only. */
  public async deleteHostTutorCalendarEvent(tutorId: string, calendarEventId: string): Promise<void> {
    try {
      await this.axiosInstance.delete(`/tutors/${tutorId}/calendar-events/${calendarEventId}`);
    } catch (error) {
      console.error(
        `Error deleting host tutor calendar event ${calendarEventId} for tutor ${tutorId}:`,
        error,
      );
      throw error;
    }
  }

  public async createRoomBooking(request: CreateRoomBookingRequest): Promise<CreateResponse> {
    try {
      const response = await this.axiosInstance.post<CreateResponse>('/room-bookings', request);
      return response.data;
    } catch (error) {
      console.error('Error creating room booking:', error);
      throw error;
    }
  }

  /** POST /v1/host/room-bookings/manual — host-created booking with optional client or guest link. */
  public async createManualRoomBooking(
    request: CreateManualRoomBookingRequest,
  ): Promise<CreateRoomBookingResponse> {
    try {
      const response = await this.axiosInstance.post<CreateRoomBookingResponse>(
        '/room-bookings/manual',
        request,
      );
      return response.data;
    } catch (error) {
      console.error('Error creating manual room booking:', error);
      throw error;
    }
  }

  public async getProfile(): Promise<HostProfile> {
    try {
      const response = await this.axiosInstance.get<HostProfile>('/profile');
      return response.data;
    } catch (error) {
      console.error('Error fetching host profile:', error);
      throw error;
    }
  }

  public async getSubscriptionPlans(): Promise<SubscriptionPlanSummary[]> {
    try {
      const response = await this.axiosInstance.get<SubscriptionPlanSummary[]>('/subscription/plans');
      return response.data;
    } catch (error) {
      console.error('Error fetching subscription plans:', error);
      throw error;
    }
  }

  public async getSubscription(): Promise<OrganizationSubscriptionStatus> {
    try {
      const response = await this.axiosInstance.get<OrganizationSubscriptionStatus>('/subscription');
      return response.data;
    } catch (error) {
      console.error('Error fetching subscription status:', error);
      throw error;
    }
  }

  public async checkoutSubscription(
    request: CreateSubscriptionCheckoutRequest,
  ): Promise<CheckoutSubscriptionResponse> {
    try {
      const response = await this.axiosInstance.post<CheckoutSubscriptionResponse>(
        '/subscription/checkout',
        request,
      );
      return response.data;
    } catch (error) {
      console.error('Error creating subscription checkout session:', error);
      throw error;
    }
  }

  public async createSubscriptionPortal(
    request?: CreateSubscriptionPortalRequest,
  ): Promise<SubscriptionPortalResponse> {
    try {
      const response = await this.axiosInstance.post<SubscriptionPortalResponse>(
        '/subscription/portal',
        request?.subscriptionPlanPriceId ? request : undefined,
      );
      return response.data;
    } catch (error) {
      console.error('Error creating subscription portal session:', error);
      throw error;
    }
  }

  /**
   * Backend-controlled plan switch (no Billing Portal redirect).
   * Used for private offered prices while a subscription is already open.
   */
  public async changeSubscriptionPlan(
    request: ChangeSubscriptionPlanRequest,
  ): Promise<SubscriptionPlanChangeResponse> {
    try {
      const response = await this.axiosInstance.post<SubscriptionPlanChangeResponse>(
        '/subscription/change',
        request,
      );
      return response.data;
    } catch (error) {
      console.error('Error changing subscription plan:', error);
      throw error;
    }
  }

  public async getSubscriptionInvoices(
    params?: GetSubscriptionInvoicesParams,
  ): Promise<SubscriptionInvoicePage> {
    try {
      const response = await this.axiosInstance.get<SubscriptionInvoicePage>(
        '/subscription/invoices',
        {
          params: {
            ...(params?.limit != null ? { limit: params.limit } : {}),
            ...(params?.startingAfter ? { startingAfter: params.startingAfter } : {}),
          },
        },
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching subscription invoices:', error);
      throw error;
    }
  }

  /** Cash-booking commission charges pending or queued for the next subscription invoice. */
  public async getCashCommissionCharges(): Promise<CashCommissionChargesResponse> {
    try {
      const response = await this.axiosInstance.get<CashCommissionChargesResponse>(
        '/subscription/cash-commission-charges',
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching cash commission charges:', error);
      throw error;
    }
  }

  /** Combined online + cash commission charges (optional channel filter). */
  public async getCommissionCharges(
    params?: GetCommissionChargesParams,
  ): Promise<CommissionChargesResponse> {
    try {
      const response = await this.axiosInstance.get<CommissionChargesResponse>(
        '/subscription/commission-charges',
        {
          params: {
            ...(params?.channel ? { channel: params.channel } : {}),
          },
        },
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching commission charges:', error);
      throw error;
    }
  }

  /** Live default payment method from Stripe (`paymentMethod` may be null). */
  public async getSubscriptionPaymentMethod(): Promise<SubscriptionPaymentMethodResponse> {
    try {
      const response = await this.axiosInstance.get<SubscriptionPaymentMethodResponse>(
        '/subscription/payment-method',
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching subscription payment method:', error);
      throw error;
    }
  }

  public async createStripeConnectExpressAccount(): Promise<StripeConnectExpressAccount> {
    try {
      const response = await this.axiosInstance.post<StripeConnectExpressAccount>('/stripe/connect-express-account');
      return response.data;
    } catch (error) {
      console.error('Error creating Stripe Connect Express account:', error);
      throw error;
    }
  }

  public async getStripeConnectExpressAccount(): Promise<StripeConnectExpressAccount> {
    try {
      const response = await this.axiosInstance.get<StripeConnectExpressAccount>('/stripe/connect-express-account');
      return response.data;
    } catch (error) {
      console.error('Error fetching Stripe Connect Express account:', error);
      throw error;
    }
  }

  public async resyncStripeConnectExpressAccount(): Promise<StripeConnectExpressAccount> {
    try {
      const response = await this.axiosInstance.post<StripeConnectExpressAccount>(
        '/stripe/connect-express-account/resync',
      );
      return response.data;
    } catch (error) {
      console.error('Error resyncing Stripe Connect Express account:', error);
      throw error;
    }
  }

  public async disconnectConnectExpress(): Promise<void> {
    try {
      await this.axiosInstance.delete<void>('/stripe/connect-express-account');
    } catch (error) {
      console.error('Error disconnecting Stripe Connect Express account:', error);
      throw error;
    }
  }

  /** Past and upcoming Stripe Connect payouts for the active organization. */
  public async getStripePayouts(params?: GetStripePayoutsParams): Promise<StripePayoutPage> {
    try {
      const response = await this.axiosInstance.get<StripePayoutPage>('/stripe/payouts', {
        params: {
          ...(params?.limit != null ? { limit: params.limit } : {}),
          ...(params?.startingAfter ? { startingAfter: params.startingAfter } : {}),
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching Stripe payouts:', error);
      throw error;
    }
  }

  public async proposeRoomBookingChange(bookingId: string, request: ProposeChangeRequest): Promise<CreateResponse> {
    try {
      const response = await this.axiosInstance.post<CreateResponse>(`/room-bookings/${bookingId}/changes`, request);
      return response.data;
    } catch (error) {
      console.error(`Error proposing change for booking ${bookingId}:`, error);
      throw error;
    }
  }

  public async cancelRoomBookingChange(bookingId: string, changeId: string, request: CancelChangeRequest): Promise<void> {
    try {
      await this.axiosInstance.post<void>(`/room-bookings/${bookingId}/changes/${changeId}/cancel`, request);
    } catch (error) {
      console.error(`Error canceling change ${changeId} for booking ${bookingId}:`, error);
      throw error;
    }
  }

  /** GET /v1/host/booking-clients — combined room + tutor booking clients (backward-compatible). */
  public async getHostBookingClients(): Promise<HostOrganizationUserResponse[]> {
    try {
      const response = await this.axiosInstance.get<HostOrganizationUserResponse[]>('/booking-clients');
      return response.data;
    } catch (error) {
      console.error('Error fetching host booking clients:', error);
      throw error;
    }
  }

  /** GET /v1/host/booking-clients/students — tutor booking clients (lesson forms + Students page). */
  public async getHostBookingClientsStudents(): Promise<HostStudent[]> {
    try {
      const response = await this.axiosInstance.get<HostStudent[]>('/booking-clients/students');
      return response.data;
    } catch (error) {
      console.error('Error fetching host student booking clients:', error);
      throw error;
    }
  }

  /** POST /v1/host/booking-clients/students — add student by phone; optionally SMS a login invite. */
  public async createHostStudent(request: CreateHostStudentRequest): Promise<CreateHostStudentResponse> {
    try {
      const response = await this.axiosInstance.post<CreateHostStudentResponse>(
        '/booking-clients/students',
        request
      );
      return response.data;
    } catch (error) {
      console.error('Error creating host student:', error);
      throw error;
    }
  }

  /**
   * PUT /v1/host/booking-clients/students/{studentUserId}/profile
   * Updates host-maintained school profile for a user already on the student list.
   */
  public async updateHostStudentProfile(
    studentUserId: string,
    request: UpdateStudentProfileRequest
  ): Promise<StudentProfile> {
    try {
      const response = await this.axiosInstance.put<StudentProfile>(
        `/booking-clients/students/${studentUserId}/profile`,
        request
      );
      return response.data;
    } catch (error) {
      console.error(`Error updating student profile for ${studentUserId}:`, error);
      throw error;
    }
  }

  /** DELETE /v1/host/booking-clients/students/{studentUserId} — remove from school roster only. */
  public async removeHostStudent(studentUserId: string): Promise<void> {
    try {
      await this.axiosInstance.delete<void>(`/booking-clients/students/${studentUserId}`);
    } catch (error) {
      console.error(`Error removing host student ${studentUserId}:`, error);
      throw error;
    }
  }

  /** GET /v1/host/booking-clients/room-renters — room booking clients (room forms + Room renters page). */
  public async getHostBookingClientsRoomRenters(): Promise<HostOrganizationUserResponse[]> {
    try {
      const response = await this.axiosInstance.get<HostOrganizationUserResponse[]>(
        '/booking-clients/room-renters'
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching host room-renter booking clients:', error);
      throw error;
    }
  }

  public async reportHostUser(userId: string, reason: string): Promise<{ id: string }> {
    try {
      const response = await this.axiosInstance.post<{ id: string }>(
        `/user-moderation/users/${userId}/reports`,
        { reason }
      );
      return response.data;
    } catch (error) {
      console.error(`Error reporting user ${userId}:`, error);
      throw error;
    }
  }

  public async removeHostUserOrganizationBlacklist(userId: string): Promise<void> {
    try {
      await this.axiosInstance.delete<void>(`/user-moderation/users/${userId}/blacklist`);
    } catch (error) {
      console.error(`Error removing org blacklist for user ${userId}:`, error);
      throw error;
    }
  }

  /** Host monthly earnings for the active organization. `month` must be in `YYYY-MM` format. */
  public async getHostMonthlyEarnings(month: string): Promise<HostMonthlyEarningsResponse> {
    try {
      const response = await this.axiosInstance.get<HostMonthlyEarningsResponse>(
        '/organization/earnings',
        { params: { month } },
      );
      return response.data;
    } catch (error) {
      console.error(`Error fetching host monthly earnings for ${month}:`, error);
      throw error;
    }
  }

  public async getSmartLockSettings(): Promise<SmartLockSettingsResponse> {
    try {
      const response = await this.axiosInstance.get<SmartLockSettingsResponse>('/smart-lock/settings');
      return response.data;
    } catch (error) {
      console.error('Error fetching smart lock settings:', error);
      throw error;
    }
  }

  public async upsertSmartLockSettings(request: UpsertSmartLockSettingsRequest): Promise<void> {
    try {
      await this.axiosInstance.put<void>('/smart-lock/settings', request);
    } catch (error) {
      console.error('Error upserting smart lock settings:', error);
      throw error;
    }
  }

  public async deleteSmartLockSettings(): Promise<void> {
    try {
      await this.axiosInstance.delete<void>('/smart-lock/settings');
    } catch (error) {
      console.error('Error deleting smart lock settings:', error);
      throw error;
    }
  }

  public async getSmartLocks(): Promise<SmartLockSummary[]> {
    try {
      const response = await this.axiosInstance.get<SmartLockSummary[]>('/smart-lock/locks');
      return response.data;
    } catch (error) {
      console.error('Error fetching smart locks:', error);
      throw error;
    }
  }

  public async linkRoomToSmartLock(roomId: string, request: LinkRoomToSmartLockRequest): Promise<LinkedRoomLockResponse> {
    try {
      const response = await this.axiosInstance.put<LinkedRoomLockResponse>(`/smart-lock/rooms/${roomId}`, request);
      return response.data;
    } catch (error) {
      console.error(`Error linking room ${roomId} to smart lock:`, error);
      throw error;
    }
  }

  public async unlinkRoomFromSmartLock(roomId: string): Promise<void> {
    try {
      await this.axiosInstance.delete<void>(`/smart-lock/rooms/${roomId}`);
    } catch (error) {
      console.error(`Error unlinking room ${roomId} from smart lock:`, error);
      throw error;
    }
  }

  public async getLinkedSmartLockRooms(): Promise<LinkedRoomsByLockResponse[]> {
    try {
      const response = await this.axiosInstance.get<LinkedRoomsByLockResponse[]>('/smart-lock/linked-rooms');
      return response.data;
    } catch (error) {
      console.error('Error fetching linked smart lock rooms:', error);
      throw error;
    }
  }

  public async generateSmartLockPasscode(
    request: GenerateSmartLockPasscodeRequest
  ): Promise<GenerateSmartLockPasscodeResponse> {
    try {
      const response = await this.axiosInstance.post<GenerateSmartLockPasscodeResponse>('/smart-lock/passcode', request);
      return response.data;
    } catch (error) {
      console.error('Error generating smart lock passcode:', error);
      throw error;
    }
  }

  /** GET /v1/host/passes — optional status filter (omit = ACTIVE+INACTIVE). REMOVED is never used in FE. */
  public async listPassOffers(statuses?: PassOfferStatus[]): Promise<PassOfferSummary[]> {
    try {
      const response = await this.axiosInstance.get<PassOfferSummary[]>('/passes', {
        params: statuses?.length ? { status: statuses } : undefined,
        paramsSerializer: { indexes: null },
      });
      // Defensive: REMOVED is backend-only soft delete and must never appear in UI.
      const userFacing = new Set<PassOfferStatus>(['ACTIVE', 'INACTIVE', 'ARCHIVED']);
      return response.data.filter((offer) => userFacing.has(offer.status));
    } catch (error) {
      console.error('Error fetching host pass offers:', error);
      throw error;
    }
  }

  /** GET /v1/host/passes/{passId} */
  public async getPassOffer(passId: string): Promise<PassOfferDetail> {
    try {
      const response = await this.axiosInstance.get<PassOfferDetail>(`/passes/${passId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching host pass offer ${passId}:`, error);
      throw error;
    }
  }

  /** GET /v1/host/passes/{passId}/instances — all sold instances for the offer (any status). */
  public async listPassOfferInstances(passId: string): Promise<PassProviderInstanceSummary[]> {
    try {
      const response = await this.axiosInstance.get<PassProviderInstanceSummary[]>(
        `/passes/${passId}/instances`
      );
      return response.data;
    } catch (error) {
      console.error(`Error fetching host pass offer instances ${passId}:`, error);
      throw error;
    }
  }

  /** POST /v1/host/pass-purchases/{purchaseId}/activate — issue cash pass and mark payment collected. */
  public async activatePassPurchase(purchaseId: string): Promise<void> {
    try {
      await this.axiosInstance.post<void>(`/pass-purchases/${purchaseId}/activate`);
    } catch (error) {
      console.error(`Error activating pass purchase ${purchaseId}:`, error);
      throw error;
    }
  }

  /** POST /v1/host/passes */
  public async createPassOffer(request: PassOfferUpsertRequest): Promise<PassOfferDetail> {
    try {
      const response = await this.axiosInstance.post<PassOfferDetail>('/passes', request);
      return response.data;
    } catch (error) {
      console.error('Error creating host pass offer:', error);
      throw error;
    }
  }

  /** PUT /v1/host/passes/{passId} */
  public async updatePassOffer(passId: string, request: PassOfferUpsertRequest): Promise<PassOfferDetail> {
    try {
      const response = await this.axiosInstance.put<PassOfferDetail>(`/passes/${passId}`, request);
      return response.data;
    } catch (error) {
      console.error(`Error updating host pass offer ${passId}:`, error);
      throw error;
    }
  }

  /** POST /v1/host/passes/{passId}/activate */
  public async activatePassOffer(passId: string): Promise<PassOfferDetail> {
    try {
      const response = await this.axiosInstance.post<PassOfferDetail>(`/passes/${passId}/activate`);
      return response.data;
    } catch (error) {
      console.error(`Error activating host pass offer ${passId}:`, error);
      throw error;
    }
  }

  /** POST /v1/host/passes/{passId}/deactivate */
  public async deactivatePassOffer(passId: string): Promise<PassOfferDetail> {
    try {
      const response = await this.axiosInstance.post<PassOfferDetail>(`/passes/${passId}/deactivate`);
      return response.data;
    } catch (error) {
      console.error(`Error deactivating host pass offer ${passId}:`, error);
      throw error;
    }
  }

  /** POST /v1/host/passes/{passId}/archive */
  public async archivePassOffer(passId: string): Promise<PassOfferDetail> {
    try {
      const response = await this.axiosInstance.post<PassOfferDetail>(`/passes/${passId}/archive`);
      return response.data;
    } catch (error) {
      console.error(`Error archiving host pass offer ${passId}:`, error);
      throw error;
    }
  }

  /** POST /v1/host/passes/{passId}/restore — ARCHIVED → INACTIVE (draft). */
  public async restorePassOffer(passId: string): Promise<PassOfferDetail> {
    try {
      const response = await this.axiosInstance.post<PassOfferDetail>(`/passes/${passId}/restore`);
      return response.data;
    } catch (error) {
      console.error(`Error restoring host pass offer ${passId}:`, error);
      throw error;
    }
  }

  /** POST /v1/host/passes/{passId}/remove — backend soft-delete (REMOVED). Treat as gone in UI. */
  public async removePassOffer(passId: string): Promise<void> {
    try {
      await this.axiosInstance.post(`/passes/${passId}/remove`);
    } catch (error) {
      console.error(`Error removing host pass offer ${passId}:`, error);
      throw error;
    }
  }

  /** GET /v1/host/pass-instances/{passId} — booked pass instance (not offer catalog / owner wallet). */
  public async getPassInstance(passId: string): Promise<PassProviderDetail> {
    try {
      const response = await this.axiosInstance.get<PassProviderDetail>(`/pass-instances/${passId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching host pass instance ${passId}:`, error);
      throw error;
    }
  }

  /** GET /v1/host/pass-instances/{passId}/events */
  public async getPassInstanceEvents(passId: string): Promise<PassProviderEvent[]> {
    try {
      const response = await this.axiosInstance.get<PassProviderEvent[]>(`/pass-instances/${passId}/events`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching host pass instance events ${passId}:`, error);
      throw error;
    }
  }

  /** GET /v1/host/lesson-options — optional status filter (omit = ACTIVE+INACTIVE; never returns REMOVED). */
  public async listLessonOptions(statuses?: LessonOptionStatus[]): Promise<LessonOptionSummaryResponse[]> {
    try {
      const response = await this.axiosInstance.get<LessonOptionSummaryResponse[]>('/lesson-options', {
        params: statuses?.length ? { status: statuses } : undefined,
        paramsSerializer: { indexes: null },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching host lesson options:', error);
      throw error;
    }
  }

  /** GET /v1/host/lesson-options/{lessonOptionId} */
  public async getLessonOption(lessonOptionId: string): Promise<LessonOptionDetailResponse> {
    try {
      const response = await this.axiosInstance.get<LessonOptionDetailResponse>(
        `/lesson-options/${lessonOptionId}`
      );
      return response.data;
    } catch (error) {
      console.error(`Error fetching host lesson option ${lessonOptionId}:`, error);
      throw error;
    }
  }

  /** POST /v1/host/lesson-options */
  public async createLessonOption(request: LessonOptionUpsertRequest): Promise<LessonOptionDetailResponse> {
    try {
      const response = await this.axiosInstance.post<LessonOptionDetailResponse>('/lesson-options', request);
      return response.data;
    } catch (error) {
      console.error('Error creating host lesson option:', error);
      throw error;
    }
  }

  /** PUT /v1/host/lesson-options/{lessonOptionId} */
  public async updateLessonOption(
    lessonOptionId: string,
    request: LessonOptionUpsertRequest
  ): Promise<LessonOptionDetailResponse> {
    try {
      const response = await this.axiosInstance.put<LessonOptionDetailResponse>(
        `/lesson-options/${lessonOptionId}`,
        request
      );
      return response.data;
    } catch (error) {
      console.error(`Error updating host lesson option ${lessonOptionId}:`, error);
      throw error;
    }
  }

  /** POST /v1/host/lesson-options/{lessonOptionId}/activate */
  public async activateLessonOption(lessonOptionId: string): Promise<LessonOptionDetailResponse> {
    try {
      const response = await this.axiosInstance.post<LessonOptionDetailResponse>(
        `/lesson-options/${lessonOptionId}/activate`
      );
      return response.data;
    } catch (error) {
      console.error(`Error activating host lesson option ${lessonOptionId}:`, error);
      throw error;
    }
  }

  /** POST /v1/host/lesson-options/{lessonOptionId}/deactivate */
  public async deactivateLessonOption(lessonOptionId: string): Promise<LessonOptionDetailResponse> {
    try {
      const response = await this.axiosInstance.post<LessonOptionDetailResponse>(
        `/lesson-options/${lessonOptionId}/deactivate`
      );
      return response.data;
    } catch (error) {
      console.error(`Error deactivating host lesson option ${lessonOptionId}:`, error);
      throw error;
    }
  }

  /** POST /v1/host/lesson-options/{lessonOptionId}/archive */
  public async archiveLessonOption(lessonOptionId: string): Promise<LessonOptionDetailResponse> {
    try {
      const response = await this.axiosInstance.post<LessonOptionDetailResponse>(
        `/lesson-options/${lessonOptionId}/archive`
      );
      return response.data;
    } catch (error) {
      console.error(`Error archiving host lesson option ${lessonOptionId}:`, error);
      throw error;
    }
  }

  /** POST /v1/host/lesson-options/{lessonOptionId}/restore — ARCHIVED → INACTIVE (draft). */
  public async restoreLessonOption(lessonOptionId: string): Promise<LessonOptionDetailResponse> {
    try {
      const response = await this.axiosInstance.post<LessonOptionDetailResponse>(
        `/lesson-options/${lessonOptionId}/restore`
      );
      return response.data;
    } catch (error) {
      console.error(`Error restoring host lesson option ${lessonOptionId}:`, error);
      throw error;
    }
  }

  /** POST /v1/host/lesson-options/{lessonOptionId}/remove — backend soft-delete (REMOVED). Treat as gone in UI. */
  public async removeLessonOption(lessonOptionId: string): Promise<void> {
    try {
      await this.axiosInstance.post(`/lesson-options/${lessonOptionId}/remove`);
    } catch (error) {
      console.error(`Error removing host lesson option ${lessonOptionId}:`, error);
      throw error;
    }
  }

  /** POST /v1/host/lesson-options/{lessonOptionId}/tutors/{tutorId} */
  public async assignLessonOptionTutor(
    lessonOptionId: string,
    tutorId: string
  ): Promise<LessonOptionDetailResponse> {
    try {
      const response = await this.axiosInstance.post<LessonOptionDetailResponse>(
        `/lesson-options/${lessonOptionId}/tutors/${tutorId}`
      );
      return response.data;
    } catch (error) {
      console.error(`Error assigning tutor ${tutorId} to lesson option ${lessonOptionId}:`, error);
      throw error;
    }
  }

  /** DELETE /v1/host/lesson-options/{lessonOptionId}/tutors/{tutorId} */
  public async unassignLessonOptionTutor(
    lessonOptionId: string,
    tutorId: string
  ): Promise<LessonOptionDetailResponse> {
    try {
      const response = await this.axiosInstance.delete<LessonOptionDetailResponse>(
        `/lesson-options/${lessonOptionId}/tutors/${tutorId}`
      );
      return response.data;
    } catch (error) {
      console.error(`Error unassigning tutor ${tutorId} from lesson option ${lessonOptionId}:`, error);
      throw error;
    }
  }
}


export default HostApiClient;

export const hostApiClient = new HostApiClient();
